import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCookie, setCookie, hasCookieConsent } from '../utils/cookieUtils';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check cookie first if consent is given
    if (hasCookieConsent()) {
      const cookieTheme = getCookie('theme') as Theme;
      if (cookieTheme === 'light' || cookieTheme === 'dark') {
        return cookieTheme;
      }
    }
    // Fallback to localStorage for backward compatibility
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    
    // Save to cookie if consent is given
    if (hasCookieConsent()) {
      setCookie('theme', theme, 365);
      // Migrate from localStorage to cookie if needed
      const localTheme = localStorage.getItem('theme');
      if (localTheme) {
        localStorage.removeItem('theme');
      }
    } else {
      // Fallback to localStorage if no consent
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  // Migrate from localStorage to cookie when consent is given
  useEffect(() => {
    const migrateTheme = (): void => {
      if (hasCookieConsent()) {
        const localTheme = localStorage.getItem('theme') as Theme;
        if (localTheme && (localTheme === 'light' || localTheme === 'dark')) {
          setCookie('theme', localTheme, 365);
          localStorage.removeItem('theme');
          // Update theme if it differs
          if (localTheme !== theme) {
            setTheme(localTheme);
          }
        } else if (!getCookie('theme')) {
          // Save current theme to cookie if no cookie exists
          setCookie('theme', theme, 365);
        }
      }
    };

    migrateTheme();

    // Listen for cookie consent changes
    const handleConsentChange = (): void => {
      migrateTheme();
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, [theme]);

  const toggleTheme = (): void => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      // Immediately save to cookie if consent is given
      if (hasCookieConsent()) {
        setCookie('theme', newTheme, 365);
      }
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

